import { DataGridPro as DataGrid, GridCellEditCommitParams, GridRenderCellParams, GridSortModel, GridCallbackDetails,
    GridColumnResizeParams, GridColumnHeaderParams, GridFilterModel, GridFilterItem, GridLinkOperator,
    GridValueFormatterParams, GridCellParams, GridRowParams, GridColumnVisibilityModel,
    GridRowHeightParams, GridPinnedColumns, GridRowId, useGridApiRef, GridCellModes, GridCellValue}
    from '@mui/x-data-grid-pro';
import { Label, Schema, TextFields } from "@mui/icons-material";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import AddColumnDialog from "./AddColumnDialog";
import AddSheetDialog from "./AddSheetDialog";
import { makeStyles } from "@mui/styles";
import { Box, Typography } from "@mui/material";
import { Network } from "../../fstn/network";
import { selectComments, selectConstants, selectProjects, selectSheets, selectTags } from "../../redux/selectors/firebase";
import { Constants, ITag, Tags } from "../graph/StateTags";
import { getConstant, getTagDisplayValue } from "../../utility/constantsTagsUtils";
import { FirebaseContext } from "../firebase/FirebaseProvider";
import { selectModeData, selectProject } from "../../redux/selectors/editor";
import SheetTabs from "./SheetTabs";
import SheetToolbar from "./SheetToolbar";
import SheetColumnMenu from './SheetColumnMenu';
import { State } from '../../fstn/state';
import BooleanConstantCell, {BooleanCellColDef} from './cells/BooleanConstantCell';
import GroupExpandCell from './cells/GroupExpandCell';
import NumberCell from './cells/NumberCell';
import { createFilter, createTagFilter, noGroup } from './gridViewFilters';
import { clearURLSearchParam, getURLSearchParam } from '../../utility/url';
import { Comments } from '../comments/CommentContainer';
import SheetContextMenu, { Cell } from './SheetContextMenu';
import SheetComments from '../comments/SheetComments';
import {ISheetModeData, updateFocus, updateMode, updateModeData} from '../../redux/slices/editor';
import LinkCell from './cells/LinkCell';
import WrapTextCell from './cells/WrapTextCell';
import { autoColDefs, ColumnData, CustomGridColDef, StateDataTypeKeys, stateDataTypes } from './gridColumnDefs';
import { ConditionalFormattingDefinition } from './conditionalFormatting/ConditionalFormattingItem';
import ConditionalFormattingStyles from './conditionalFormatting/ConditionalFormattingStyles';
import ConditionalFormatting, { getActiveFormatClassNames } from './conditionalFormatting/ConditionalFormatting';
import { CellType, isTagEntry, RowDef, TagCellType } from './cells/cellUtils';
import { numberSort, stringSort } from './gridViewSort';
import SheetFilters from './SheetFilters';
import SheetInspector from './SheetInspector';
import { SideBar } from '../sidebar/SideBar';
import { Cursor } from '../../fstn/cursor';
import { Projects } from '../../redux/types/firebase';
import { OrderConstraintValues } from '../dialogs/project/OrderableData';
import {unique} from "../../utility/array";
import {GridSortItem} from "@mui/x-data-grid/models/gridSortModel";
import {getBlockChapter, getBlockID, getBlockNumber} from "../../utility/blockNames";

const useStyles = makeStyles((theme) => ({
    content: {
        flexGrow: 1,
        height: "100%"
    },
    root: {
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        height: "100%"
    },
    rowOdd: {
        backgroundColor: "rgba(235, 235, 235, .7)"
    },
    cellComment: {
        position: "relative",
        "&:after": {
            content: "''",
            width: 0,
            height: 0,
            borderStyle: "solid",
            borderWidth: "0 10px 10px 0",
            borderColor: `transparent ${theme.palette.cellComment.main} transparent transparent`,
            right: 0,
            top: 0,
            position: "absolute"
        }
    },
    drawer: {
        flexShrink: 0,
        width: "240px"
    },
    gridView: {
        "& .MuiBadge-anchorOriginTopRightRectangular": {
            top: "14px"
        },
        "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: theme.palette.cellSelected.main,
        },
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%"
    }
}))
const sideBarTabs = [
    {
        name: "Inspector",
        type: SheetInspector
    },
    {
        name: "Filters",
        type: SheetFilters
    },
    {
        name: "Conditional Formatting",
        type: ConditionalFormatting
    }
];

function filterStateRows(entry: GridRowId)
{
    return !String(entry).startsWith("groups_");
}

function mapSelectionToStates(entry: GridRowId)
{
    const rowId = String(entry);
    if (rowId.toString().includes("_"))
    {
        const [rowContext, id] = rowId.split("_");
        if (rowContext !== "groups")
            return id;
    }

    return rowId
}

export interface IGridFilterItem extends GridFilterItem {
    inactive?: boolean
    caseSensitive?: boolean
    columnField: string
    locked?: boolean
    operatorValue?: string
    value?: any
}
export interface Sheet
{
    name: string,
    order?: number,
    description?: string,
    group?: string
    wrapText?: boolean
    columns: { [id: string]: ColumnData }
    rowData: { [field: string]: { [columnId: string]: CellType } }
    conditionalFormatting: { [ruleId: string]: ConditionalFormattingDefinition }
    filterModel?: { linkOperator: GridLinkOperator, items: { [index: string]: IGridFilterItem } },
    sortModel?: GridSortItem[]
}

export interface Sheets
{
    [sheetId: string]: Sheet
}

interface Props
{
    tags: Tags
    comments: Comments
    constants: Constants
    network: Network
    cursor: Cursor
    sheets: Sheets
    project: string
    projects: Projects
    modeData: ISheetModeData
    updateFocus: (focus: string) => void
    updateMode: (mode: string) => void
    updateModeData: (modeData: Partial<ISheetModeData>) => void
}

function GridView({ project, projects, network, cursor, sheets, tags, constants, comments, modeData, updateFocus, updateMode, updateModeData }: Props)
{
    const classes = useStyles();
    const { get, set, trackSheets, untrack, push, trackComments } = useContext(FirebaseContext);
    const gridApi = useGridApiRef();

    const contentRef = useRef<HTMLDivElement>(null);
    const grid = useRef<HTMLDivElement>(null);

    const [columnDialogOpen, setColumnDialogOpen] = useState(false);
    const [addSheetDialogOpen, setAddSheetDialogOpen] = useState(false);

    const { sheetId, selection, selectedCell, expanded, groupedViewEnabled } = modeData;

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [contextCellTarget, setContextCellTarget] = useState<HTMLElement | null>(null);
    const [contextCell, setContextCell] = useState<Cell>({ row: "", column: "" });

    const [commentCellTarget, setCommentCellTarget] = useState<HTMLElement | null>(null);
    const [commentCell, setCommentCell] = useState<Cell>({ row: "", column: "" });

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

    const [editing, setEditing] = useState(false);

    const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumns>();
    const [width, setWidth] = useState("100%");
    const sheet = useMemo(() =>
    {
        if (sheets && sheetId && sheetId in sheets)
        {
            return sheets[sheetId];
        }
        else
        {
            return { name: "Sheet 1", columns: {}, rowData: {}, conditionalFormatting: {} } as Sheet;
        }
    }, [sheetId, sheets]);

    const filterModel: GridFilterModel = useMemo(() =>
    {
        const items = Object.entries(sheet?.filterModel?.items ?? []).reduce((items, [id, filterItem]) =>
        {
            items.push({ id, ...filterItem });

            return items;
        }, [] as IGridFilterItem[]);

        return {
            items,
            linkOperator: sheet.filterModel?.linkOperator
        }
    }, [sheet]);

    const statesSelection = useMemo(() =>
    {
        const states = selection?.filter(filterStateRows).map(mapSelectionToStates) ?? [];
        return Array.from(new Set(states));
    }, [selection]);

    // Lookup for if a cell has a comment
    const cellCommentsMap = useMemo(() =>
    {
        return Object.entries(comments ?? {}).reduce((map, [id, comment]) =>
        {
            if (comment.type.name === "sheet" && !comment.deleted && !comment.resolved)
            {
                const { row, column } = comment.type;
                map[row] = map[row] ?? {};
                map[row][column] = true;
            }

            return map;
        }, {} as { [row: string]: { [column: string]: boolean } });
    }, [comments]);

    const handleClickIdLink = useCallback((event: React.MouseEvent<HTMLElement>) =>
    {
        const id = event.currentTarget.dataset.value;

        if (id)
        {
            updateMode("graph");
            updateFocus(id);
        }
    }, [updateMode, updateFocus]);

    const [cellHeights, setCellHeights] = useState<{ [id: string]: { [field: string]: number } }>({});

    const handleSetCellHeight = useCallback((id: string, field: string, height: number) =>
    {
        setCellHeights((cellHeights) =>
        {
            cellHeights[id] = cellHeights[id] ?? {}

            if (!(field in cellHeights[id]) || Math.abs(cellHeights[id][field] - height) > 3)
            {
                cellHeights[id][field] = height;
                return { ...cellHeights };
            }

            return cellHeights;
        });
    }, []);
    const handleVisibilityModelChange = (model: GridColumnVisibilityModel) => {
        Object.keys(model).forEach(field => {
            const column = columns.find(column => column.field === field);

            if (column && column.hidden === model[field]) {
                set(`sheets/${project}/${sheetId}/columns/${column.id}/hidden`, !column.hidden).catch(console.error);
            } 
        });
        setColumnVisibilityModel(model);
    }
    const GroupCell = useCallback((props: GridRenderCellParams) => <GroupExpandCell {...props} />, []);
    const BooleanConstantValueCell = useCallback((props: GridRenderCellParams) => <BooleanConstantCell {...props} />, []);
    const NumberValueCell = useCallback((props: GridRenderCellParams) => <NumberCell {...props} />, []);
    const LinkValueCell = useCallback((props: GridRenderCellParams) => <LinkCell {...props} />, []);

    const GroupHeaderCell = useCallback((params: GridColumnHeaderParams) => <><Typography variant={"body2"}>{params.colDef.headerName}</Typography></>, []);
    const ConstantHeaderCell = useCallback((params: GridColumnHeaderParams) => <><Label fontSize={"small"} /><Typography variant={"body2"}>{params.colDef.headerName}</Typography></>, []);
    const StateFieldHeaderCell = useCallback((params: GridColumnHeaderParams) => <><Schema fontSize={"small"} /><Typography variant={"body2"}>{params.colDef.headerName}</Typography></>, []);
    const CustomFieldHeaderCell = useCallback((params: GridColumnHeaderParams) => <><TextFields fontSize={"small"} /><Typography variant={"body2"}>{params.colDef.headerName}</Typography></>, []);

    const StringWrapTextCell = useCallback((props: GridRenderCellParams) => <WrapTextCell {...props} />, []);

    const defaultStateDataColumnProps: CustomGridColDef = useMemo(() => ({
        editable: false,
        order: 0,
        width: 260,
        headerAlign: "left",
        hidden: false,
        renderHeader: StateFieldHeaderCell,
        valueFormatter: (params: GridValueFormatterParams) => params.value,
        onSetHeight: handleSetCellHeight,
        sourceType: "state"
    } as CustomGridColDef), [StateFieldHeaderCell, handleSetCellHeight]);

    const stateDataColumnDefs: { [K in StateDataTypeKeys]: CustomGridColDef; } = useMemo(() => ({
        _stateId: {
            ...defaultStateDataColumnProps,
            id: '_stateId',
            hidden: false,
            field: '_stateId',
            type: "string",
            headerName: stateDataTypes["_stateId"].name,
            renderCell: LinkValueCell,
            onClick: handleClickIdLink,
        },
        _title: {
            ...defaultStateDataColumnProps,
            id: '_title',
            field: '_title',
            type: "string",
            headerName: stateDataTypes["_title"].name,
        },
        _script: {
            ...defaultStateDataColumnProps,
            id: '_script',
            field: '_script',
            type: "string",
            headerName: stateDataTypes["_script"].name,
        },
        _transitionCount: {
            ...defaultStateDataColumnProps,
            id: '_transitionCount',
            field: '_transitionCount',
            type: "number",
            headerName: stateDataTypes["_transitionCount"].name,
        },
        _transitionName: {
            ...defaultStateDataColumnProps,
            id: '_transitionName',
            field: '_transitionName',
            type: "string",
            headerName: stateDataTypes["_transitionName"].name,
        },
        _transitionDestinations: {
            ...defaultStateDataColumnProps,
            id: '_transitionDestinations',
            field: '_transitionDestinations',
            type: "string",
            headerName: stateDataTypes["_transitionDestinations"].name,
        },
        _blockNames: {
            ...defaultStateDataColumnProps,
            id: '_blockNames',
            field: '_blockNames',
            type: "string",
            headerName: stateDataTypes["_blockNames"].name
        },
        _blockChapters: {
            ...defaultStateDataColumnProps,
            id: '_blockChapters',
            field: '_blockChapters',
            type: "string",
            headerName: stateDataTypes["_blockChapters"].name
        },
        _blockIDs: {
            ...defaultStateDataColumnProps,
            id: '_blockIDs',
            field: '_blockIDs',
            type: "string",
            headerName: stateDataTypes["_blockIDs"].name
        },
        _blockNumbers: {
            ...defaultStateDataColumnProps,
            id: '_blockNumbers',
            field: '_blockNumbers',
            type: "string",
            headerName: stateDataTypes["_blockNumbers"].name
        }
    }), [LinkValueCell, handleClickIdLink, defaultStateDataColumnProps]);

    const constantCells = useMemo(() =>
    ({
        number: NumberValueCell,
        string: StringWrapTextCell,
        boolean: BooleanConstantValueCell
    }), [NumberValueCell, StringWrapTextCell, BooleanConstantValueCell]);

    const [conditionalFormatCells, setConditionalFormatCells] = useState<{ [cellId: string]: Array<string> }>({});

    const tagValueFormatter = useCallback((params: GridValueFormatterParams) =>
    {
        if (isTagEntry(params.value))
        {
            const tags: ITag[] = params.value.tags;
            return tags.map((tag) => tag.value !== noGroup.id ? getTagDisplayValue(tag, constants) : noGroup.value).join(", ");
        }
        if (params.value?.toLocaleString) {
            return params.value?.toLocaleString();
        } else {
            return params.value?.toString();
        }
    }, [constants]);

    const defaultColumns: CustomGridColDef[] = useMemo(() =>
    {
        const columns: CustomGridColDef[] = [];

        if (sheet.group)
        {
            columns.unshift({
                id: '_group',
                field: '_group',
                headerName: autoColDefs["_group"].name,
                headerAlign: "left",
                type: "string",
                order: -1,
                editable: false,
                width: 200,
                hidden: false,
                textWrap: false,
                filterable: false,
                sortable: false,
                renderCell: GroupCell,
                renderHeader: GroupHeaderCell,
                valueFormatter: tagValueFormatter,
                onSetHeight: handleSetCellHeight,
                sourceType: "auto",
            });
        }

        return columns;
    }, [sheet.group, GroupCell, GroupHeaderCell, tagValueFormatter, handleSetCellHeight]);

    const insertColumnType = useCallback((id: string, column: ColumnData) =>
    {
        if (!column.columnType)
        {
            const convertColumn = column as any;
            const path = `sheets/${project}/${sheetId}/columns/${id}/columnType`;
            if (convertColumn.source)
            {
                if (convertColumn.source === "custom")
                {
                    set(path, "custom");
                }
                else if (convertColumn.source.startsWith("_"))
                {
                    set(path, "state");
                }
                else if (convertColumn.source in constants)
                {
                    set(path, "constant");
                }
            }
            else if (defaultColumns.find((col) => col.id === id))
            {
                set(path, "auto");
            }
        }
    }, [set, constants, project, sheetId, defaultColumns]);

    const getGroupColumnName = useCallback(() =>
    {
        return sheet.group ? getConstant(sheet.group, constants)?.name : ""
    }, [sheet, constants]);

    const columns = useMemo(() =>
    {
        const sheetColumns = Object.entries(sheet.columns ?? {});

        const columns = sheetColumns.map(([id, column]) =>
        {
            insertColumnType(id, column);

            switch (column.columnType)
            {
                case "auto":
                    const defaultColumn = defaultColumns.find((defaultColumn) => defaultColumn.id === id);

                    return defaultColumn ? {
                        ...defaultColumn,
                        headerName: id !== "_group" ? defaultColumn?.headerName : `Group: ${getGroupColumnName()} `,
                        order: column.order ?? sheetColumns.length,
                        width: column.width ? column.width : 200,
                        onSetHeight: handleSetCellHeight,
                        sourceType: column.columnType,
                    } : null;
                case "custom":
                    const customColumn: CustomGridColDef = {
                        id,
                        field: id,
                        headerName: column.name,
                        headerAlign: "left",
                        type: column.type,
                        order: column.order ?? sheetColumns.length,
                        width: column.width ? column.width : 200,
                        hidden: column.hidden || false,
                        textWrap: column.type === "string" && (column.textWrap ?? true),
                        editable: true,
                        renderHeader: CustomFieldHeaderCell,
                        onSetHeight: handleSetCellHeight,
                        sourceType: column.columnType,
                    };

                    if (column.type === "string" || column.type === "singleSelect")
                    {
                        customColumn.renderCell = StringWrapTextCell;
                        const valueOptions = Object.entries(column.valueOptions ?? {}).map(([value, label]) => ({ value, label }));

                        if (valueOptions.length > 0)
                        {
                            customColumn.valueOptions = valueOptions;
                            customColumn.valueFormatter = (params: GridValueFormatterParams) => valueOptions.find((option) => option.value === params.value)?.label ?? "";
                            customColumn.valueParser = (value: GridCellValue) => valueOptions.find((option) => option.label === value)?.value ?? value;
                        }
                    }

                    return customColumn;
                case "state":
                    const stateColumnDef = stateDataColumnDefs[column.source as StateDataTypeKeys] ||
                        { name: "Missing", field: "", type: "string"};
                    const stateColumn: CustomGridColDef = {
                        ...stateColumnDef,
                        id,
                        order: column.order ?? sheetColumns.length,
                        width: column.width ? column.width : 200,
                        hidden: column.hidden || false,
                        textWrap: stateColumnDef.type === "string" && (column.textWrap ?? false),
                        onSetHeight: handleSetCellHeight,
                        sourceType: column.columnType
                    }

                    if (stateColumnDef.type === "string" && stateColumn.renderCell === undefined)
                    {
                        stateColumn.renderCell = StringWrapTextCell;
                    }

                    return stateColumn;
                case "constant":
                    const constant = constants[column.source];
                    const constantColumn: CustomGridColDef = {
                        id,
                        field: column.source,
                        headerName: constant?.name || column.source,
                        headerAlign: "left",
                        type: constant?.type || "string",
                        order: column.order ?? sheetColumns.length,
                        width: column.width ? column.width : 200,
                        hidden: column.hidden || false,
                        textWrap: column.textWrap ?? false,
                        editable: false,
                        renderHeader: ConstantHeaderCell,
                        renderCell: constantCells[constant?.type as keyof typeof constantCells || "string"],
                        valueFormatter: tagValueFormatter,
                        onSetHeight: handleSetCellHeight,
                        sourceType: column.columnType
                    };

                    if (constant === undefined) {
                        console.error(`There is no constant corresponding to the column ${column.source}`);
                    } else if (constant.type === "boolean" &&
                        Object.values(constant.constraints || {}).some(constraint => constraint.type === "unique")) {
                        (constantColumn as BooleanCellColDef).setTag = (state, tag, value) =>
                            set(`tags/${project}/${state}/${tag}/value`, value);
                        (constantColumn as BooleanCellColDef).addTag = (state, type, value) =>
                            push(`tags/${project}/${state}`, {type, value});
                    }
                    return constantColumn;
                default:
                    return null;
            }
        }).filter((column) => column !== null) as CustomGridColDef[];

        defaultColumns.forEach((defaultColumn) =>
        {
            if (!columns.some((column) => column.id === defaultColumn.id))
            {
                columns.push({
                    ...defaultColumn,
                    order: -1,
                    width: 200,
                });
            }
        });

        return columns;
    }, [sheet, defaultColumns, insertColumnType, stateDataColumnDefs, constants, constantCells,
        CustomFieldHeaderCell, ConstantHeaderCell, StringWrapTextCell, project, push, set,
        tagValueFormatter, getGroupColumnName, handleSetCellHeight]);

    const [sortedColumns, setSortedColumns] = useState<CustomGridColDef[]>([]);
    useEffect(() =>
    {
        if (columns.length) {
            setSortedColumns(columns.sort((a, b) =>
            {
                return a.order - b.order;
            }));
            setColumnVisibilityModel(Object.fromEntries(columns.map(column =>
                [column.field, ! column.hidden]
            )));
        }
    }, [columns]);

    const setColumnOrder = useCallback((id: string, order: number) =>
    {
        return set(`sheets/${project}/${sheetId}/columns/${id}/order`, order);
    }, [project, sheetId, set]);

    const setColumnWidth = useCallback((id: string, width: number | null) =>
    {
        return set(`sheets/${project}/${sheetId}/columns/${id}/width`, width);
    }, [project, sheetId, set]);

    const handleClickAddColumn = () =>
    {
        setColumnDialogOpen(true);
    }

    const closeColumnDialog = () =>
    {
        setColumnDialogOpen(false);
    }

    const closeAddSheetDialog = () =>
    {
        setAddSheetDialogOpen(false);
    }

    const setCell = useCallback((id: string, field: string, value: any) =>
    {
        set(`sheets/${project}/${sheetId}/rowData/${id}/${field}`, value instanceof Date ? value.getTime() : value);
    }, [project, sheetId, set]);

    const handleCopy = useCallback((rowId: string, field: string) =>
    {
        const cell = gridApi.current.getCellParams(rowId, field);
        navigator.clipboard.writeText(cell?.formattedValue ?? "");
    }, [gridApi]);

    const handlePaste = useCallback((rowId: string, field: string) =>
    {
        const cell = gridApi.current.getCellParams(rowId, field);
        if (cell?.isEditable)
        {
            gridApi.current.setCellMode(rowId, field, GridCellModes.Edit);

            navigator.clipboard.readText().then((value) =>
            {
                gridApi.current.setEditCellValue({ id: rowId, field, value });
            });
        }
    }, [gridApi]);

    const handleEditCellCommit = useCallback(({ field, id, value }: GridCellEditCommitParams, event: any, details: GridCallbackDetails) =>
    {
        const strId = String(id);
        const [, rowId] = strId.includes("_") ? String(id).split("_") : ["", strId];
        if (value !== undefined && columns.some((column) => column.field === field))
        {
            setCell(rowId, field, value);
        }
    }, [columns, setCell]);

    const handleCellEditStart = useCallback(() =>
    {
        setEditing(true);
    }, []);

    const handleCellEditStop = useCallback(() =>
    {
        setEditing(false);
    }, []);

    const handleClickAddSheet = useCallback(() =>
    {
        setAddSheetDialogOpen(true);
    }, [setAddSheetDialogOpen]);

    const handleClickToggleGroup = useCallback(() =>
    {
        updateModeData({ groupedViewEnabled: !groupedViewEnabled });
    }, [updateModeData, groupedViewEnabled]);

    const handleChangeActive = (event: React.ChangeEvent<{}>, newValue: string) =>
    {
        updateModeData({ sheetId: newValue });
    }

    const getTagsForState = useCallback((stateId: string, stateTags: { [columnId: string]: CellType } = {}): { [columnId: string]: CellType } =>
    {
        if (!tags)
            return {};

        return Object.entries(tags[stateId] ?? {}).reduce((data, [id, tag]) =>
        {
            if (!(tag.type in data))
            {
                (data[tag.type] as TagCellType) = { tags: [{...tag, id}] };
            }
            else
            {
                if (!(data[tag.type] as TagCellType).tags.some((existingTag) => existingTag.type === tag.type && existingTag.value === tag.value))
                {
                    (data[tag.type] as TagCellType).tags.push({...tag, id});
                }
            }
            return data;
        }, stateTags);
    }, [tags])

    const convertDates = useCallback((data: { [key: string]: any }) =>
    {
        columns.forEach((column) =>
        {
            if (data[column.field] && (column.type === "date" || column.type === "dateTime"))
            {
                const tags = data[column.field].tags as {type: string, value: number} [];

                if (tags) {
                    const tag = tags.find(({type}) => type === column.field);

                    data[column.field] = new Date(tag?.value ?? 0);
                } else {
                    data[column.field] = new Date(data[column.field] as number);
                }
            }
        });
    }, [columns])

    const getCustomRowDataForState = useCallback((state: string) =>
    {
        const extraData = sheet.rowData && state in sheet.rowData ? { ...sheet.rowData[state] } : {};
        convertDates(extraData);
        return extraData;
    }, [sheet, convertDates]);

    const getRowsForStates = useCallback((states: [string, State][]): RowDef[] =>
    {
        return states.map(([id, state]) =>
        {
            const extraData = getCustomRowDataForState(id);
            const stateTags = getTagsForState(id);

            return {
                id, index: 0, _group: null,
                _stateId: id, _title: state.name, _script: state.script,
                _transitionCount: state.transitions.length,
                _transitionName: state.transitions.map((transition) => transition.trigger).join(", "),
                _transitionDestinations: state.transitions.map((transition) => transition.destination).join(", "),
                _blockNames: state.meta.blockNames?.join(", ") ?? "",
                _blockChapters: state.meta.blockNames?.map(getBlockChapter)
                    .filter(group => group.length).filter(unique).join(", ") ?? "",
                _blockIDs: state.meta.blockNames?.map(getBlockID)
                    .filter(group => group.length).filter(unique).join(", ") ?? "",
                _blockNumbers: state.meta.blockNames?.map(getBlockNumber)
                    .filter(group => group.length).filter(unique).join(", ") ?? "",
                ...extraData, ...stateTags
            }
        });
    }, [getTagsForState, getCustomRowDataForState]);

    const createSheet = useCallback((name: string, group: string = "none") =>
    {
        const sheet: { [key: string]: any } = group === "none" ? { name } : { name, group };

        sheet.order = Object.keys(sheets).length;

        push(`sheets/${project}`, sheet).then((id) =>
        {
            if (id)
            {
                push(`sheets/${project}/${id}/columns`, { columnType: "state", source: "_stateId" }).then(() =>
                {
                    updateModeData({ sheetId: id });
                });
            }

        });
    }, [project, push, sheets, updateModeData]);

    const handleSortModelChange = (model: GridSortModel) =>
    {
        set(`sheets/${project}/${sheetId}/sortModel`, model).catch(console.error);
    }

    const handleFilterModelChange = useCallback((model: GridFilterModel) =>
    {
        if (sheetId)
        {
            model.items.forEach((item) =>
            {
                const filterItem: any = { columnField: item.columnField };

                if (item.operatorValue !== undefined)
                    filterItem.operatorValue = item.operatorValue;

                if (item.value !== undefined)
                    filterItem.value = item.value;

                if (item.id && sheet.filterModel?.items && item.id in sheet.filterModel.items)
                {
                    set(`sheets/${project}/${sheetId}/filterModel/items/${item.id}`, filterItem);
                }
                else
                {
                    push(`sheets/${project}/${sheetId}/filterModel/items/`, filterItem);
                }
            });

            if (sheet.filterModel?.linkOperator !== model.linkOperator)
            {
                set(`sheets/${project}/${sheetId}/filterModel/linkOperator`, model.linkOperator ?? GridLinkOperator.And);
            }
        }
    }, [push, set, sheet, project, sheetId]);

    const applySort = useCallback((rows: RowDef[]) =>
    {
        if (rows.length > 0)
        {
            sheet.sortModel?.forEach((sortItem) =>
            {
                const key = sortItem.field as keyof RowDef;

                const column = columns.find((column) => column.field === key);

                if (column && ! column.hidden)
                {
                    const sortComparison = column.type === "string" ? stringSort : numberSort;
                    rows.sort(sortComparison(key, sortItem, constants));
                }
            });
        }
        return rows;
    }, [sheet.sortModel, columns, constants]);

    const applyFilter = useCallback((rows: RowDef[]) =>
    {
        const filterRow = (filterItem: IGridFilterItem, row: RowDef) => {
            if (filterItem.inactive) return true;

            const key = filterItem.columnField as keyof RowDef;
            const column = columns.find((column) => column.field === key);

            if (column)
            {
                if (column.sourceType === "constant")
                {
                    const filter = createTagFilter(filterItem, constants);
                    if (filter)
                    {
                        return filter(row, key as string);
                    }
                }
                else
                {
                    const filter = createFilter(filterItem, column);
                    if (filter)
                    {
                        return filter(row[key] as never);
                    }
                }
            }
            return false;
        };
        return rows.filter(row => filterModel.linkOperator === "or" ?
                filterModel.items.some(filterItem => filterRow(filterItem, row)) :
                filterModel.items.every(filterItem => filterRow(filterItem, row)));
    }, [filterModel, columns, constants]);

    const applyGroup = useCallback((rows: RowDef[]) =>
    {
        const group = sheet.group;
        if (group && group !== noGroup.id && groupedViewEnabled)
        {
            const groups: Map<string, { rows: RowDef[] }> = new Map();

            Object.values(rows).forEach((row) =>
            {
                const value = row[group];

                if (!value)
                {
                    if (!groups.has(noGroup.id))
                        groups.set(noGroup.id, { rows: [] });
                    groups.get(noGroup.id)?.rows.push({ ...row, id: `${noGroup.id}_${row.id}` });
                }
                else if (isTagEntry(value))
                {
                    value.tags.forEach((entryValue: ITag) =>
                    {
                        const key = String(entryValue.value);
                        if (!groups.has(key))
                            groups.set(key, { rows: [] });
                        groups.get(key)?.rows.push({ ...row, id: `${key}_${row.id}` });
                    })
                }
                else
                {
                    const strValue = String(value);
                    if (!groups.has(strValue))
                        groups.set(strValue, { rows: [] });
                    groups.get(strValue)?.rows.push({ ...row, id: `${strValue}_${row.id}` });
                }
            });

            const groupedRows: RowDef[] = [];

            Array.from(groups.entries()).sort(([idA, groupA], [idB, groupB]) =>
            {
                return idA === noGroup.id ? 1 : idB === noGroup.id ? -1 : 0;
            }).forEach(([id, groupData]) =>
            {
                const extraData = sheet.rowData && id in sheet.rowData ? sheet.rowData[id] : {};
                const groupTagValues = groupData.rows.reduce((data, row) =>
                {
                    if (typeof row._stateId === "string")
                    {
                        return getTagsForState(row._stateId, data);
                    }

                    return {};
                }, {} as { [columnId: string]: CellType });
                const tag: TagCellType = { tags: [{ type: group, value: id }], meta: { count: groupData.rows.length } };
                const groupColId = `groups_${id}`;
                const groupedRow = { id: groupColId, index: 0, _stateId: null, _group: tag, _title: "", ...extraData, ...groupTagValues };
                convertDates(groupedRow);
                groupedRows.push(groupedRow);
                if (expanded !== undefined && expanded[id])
                {
                    groupedRows.push(...groupData.rows);
                }
            });

            return groupedRows;
        }

        return rows;
    }, [expanded, getTagsForState, sheet, groupedViewEnabled, convertDates]);

    const getStates = useCallback(() =>
    {
        const states = Array.from(network.states.entries());
        const projectDetails = projects[project];

        if (projectDetails.scriptSequenceTag)
        {
            const scriptSequenceTag = projectDetails.scriptSequenceTag;
            const constraints = constants[scriptSequenceTag ?? ""]?.constraints ?? {};
            const orderConstraint = Object.values(constraints).find((order) => order.type === "orderable");

            if (orderConstraint)
            {
                const scriptSequenceOrder = Array.from((orderConstraint as OrderConstraintValues)?.data?.values);
                const getScriptSequenceOrders = (state: string, tags: Tags) => {
                    return (state in tags ? Object.values(tags[state]).filter((tag) => tag.type === scriptSequenceTag)
                        .map((tag) => scriptSequenceOrder.indexOf(tag.value)) : []) ?? [];
                }

                states.sort(([idA, stateA], [idB, stateB]) => (stateA?.meta?.depth ?? Infinity) - (stateB?.meta?.depth ?? Infinity))
                    .sort(([idA, stateA], [idB, stateB]) => {
                        const sequenceA = Math.min(...getScriptSequenceOrders(idA, tags));
                        const sequenceB = Math.min(...getScriptSequenceOrders(idB, tags));

                        return sequenceA - sequenceB;
                    });
            }
        }

        return states;
    }, [constants, network.states, project, projects, tags]);

    const rows = useMemo((): RowDef[] =>
    {
        const states = getStates();

        let rows = getRowsForStates(states);
        rows = applyFilter(rows);
        rows = applySort(rows);
        rows = applyGroup(rows);

        rows.forEach((value, index) => value.index = index);
        return rows;
    }, [getStates, getRowsForStates, applyFilter, applySort, applyGroup]);

    const cellHasComments = useCallback((params: GridCellParams): boolean =>
    {
        return params.id in cellCommentsMap && cellCommentsMap[params.id][params.field];
    }, [cellCommentsMap])

    const getCellClassName = useCallback((params: GridCellParams): string =>
    {
        const styles: string[] = [];

        const colDef = params.colDef as unknown as CustomGridColDef;

        const conditionalFormatStyles = conditionalFormatCells[`${colDef.id}_${params.id}`];
        if (conditionalFormatStyles)
        {
            styles.push(...conditionalFormatStyles);
        }

        cellHasComments(params) && styles.push(classes.cellComment);

        return styles.join(" ");
    }, [cellHasComments, classes.cellComment, conditionalFormatCells]);

    const getRowClassName = useCallback((params: GridRowParams): string =>
    {
        return Boolean(params.row["index"] & 1) ? classes.rowOdd : ""
    }, [classes.rowOdd])

    const handleCellClick = (params: GridCellParams, event: React.MouseEvent<HTMLElement>) =>
    {
        if (cellHasComments(params))
        {
            const cell = { row: String(params.id), column: params.field };
            const target = event.currentTarget;
            requestAnimationFrame(() =>
            {
                setCommentCell(cell);
                setCommentCellTarget(target);
            })
        }

        updateModeData({ selectedCell: { field: params.field, row: params.row.id } });
    }

    const handleContextMenu = (event: React.SyntheticEvent<HTMLElement>) =>
    {
        const updateContext = (target: HTMLElement) => {
            if (target.getAttribute("role") === "cell") {
                const column = target?.dataset.field;
                const row = target?.parentElement?.dataset.id;

                if (row && column) {
                    let stateId: string | undefined;
                    if (row.includes("_")) {
                        const [rowContext, id] = row.split("_");
                        if (rowContext !== "groups")
                            stateId = id;
                    } else {
                        stateId = row;
                    }

                    setContextCell({row, column, stateId});
                    setContextCellTarget(target);
                    return true;
                }
            }
            return false;
        }

        const target = event.target as HTMLElement;

        if (target) {
            if (! updateContext(target)) {
                updateContext(target.parentNode as HTMLElement);
            }
        }
        event.preventDefault();
    }

    const handleCloseContextMenu = useCallback(() =>
    {
        setContextCell({ row: "", column: "" });
        setContextCellTarget(null);
    }, []);

    const handleCloseComments = useCallback(() =>
    {
        setContextCell({ row: "", column: "" });
        setCommentCellTarget(null);
    }, []);

    const handleColumnWidthChange = (params: GridColumnResizeParams) =>
    {
        const colDef = params.colDef as unknown as CustomGridColDef;
        setColumnWidth(colDef.id, params.width);
    }
    const handleColumnOrderChange = useCallback((params) =>
    {
        setSortedColumns(sortedColumns =>
        {
            const update = [...sortedColumns];
            const temp = update[params.oldIndex];

            update[params.oldIndex] = update[params.targetIndex];
            update[params.targetIndex] = temp;
            setColumnOrder(sortedColumns[params.oldIndex].id, params.targetIndex).then(() =>
                setColumnOrder(sortedColumns[params.targetIndex].id, params.oldIndex)
            );
            return update;
        });
    }, [setColumnOrder]);
    const handleSideBarSetWidth = (width: number) => {
        if (contentRef.current) {
            setWidth(`calc(100% - ${width}px)`);
        }
    }

    const getRowHeight = useCallback((params: GridRowHeightParams) =>
    {
        const defaultHeight = 52 * params.densityFactor;
        if (params.id in cellHeights)
        {
            const rowHeights = cellHeights[params.id];

            return Math.max(...Object.values(rowHeights), defaultHeight);
        }

        return defaultHeight;
    }, [cellHeights]);

    useEffect(() =>
    {
        trackSheets(project);
        return () =>
        {
            untrack(`sheets/${project}`);
        }
    }, [project, trackSheets, untrack]);

    useEffect(() =>
    {
        let isLoaded = true;
        if (Object.keys(sheets).length === 0)
        {
            get(`sheets/${project}/`).then((value) =>
            {
                if (!value && isLoaded)
                {
                    createSheet("Sheet 1");
                }
            });
        }
        return () =>
        {
            isLoaded = false;
        }
    }, [sheets, get, createSheet, project]);

    useEffect(() =>
    {
        const setSheet = (id?: string | null) =>
        {
            if (id && id in sheets)
            {
                updateModeData({ sheetId: id });
                return true;
            }

            return false;
        }
        const sheet = getURLSearchParam("sheet");
        if (sheet && setSheet(sheet))
        {
            clearURLSearchParam("sheet");
        }
        else if (!sheetId || !(sheetId in sheets))
        {
            const localSheet = localStorage.getItem(`sheet.${project}.lastViewed`);
            if (!setSheet(localSheet))
            {
                const firstSheet = Object.keys(sheets ?? {})[0];
                setSheet(firstSheet);
            }
        }
    }, [updateModeData, sheetId, sheets, project]);

    useEffect(() =>
    {
        if (sheetId)
        {
            localStorage.setItem(`sheet.${project}.lastViewed`, sheetId);
        }
    }, [sheetId, project]);

    useEffect(() =>
    {
        if (!comments)
        {
            trackComments(project);
        }
    }, [comments, project, trackComments]);

    useEffect(() =>
    {
        if (selection)
        {
            if (sheet.group && sheet.group !== noGroup.id)
            {
                if (Array.isArray(selection))
                {
                    selection.forEach((row) =>
                    {
                        const [context] = String(row).split("_");

                        if ((!expanded || !expanded[context]) && context !== "groups")
                            updateModeData({ expanded: { ...expanded, [context]: true } });

                        const index = rows.findIndex((rowDef) => rowDef.id === row);

                        if (index)
                            setPage(Math.floor(index / pageSize));
                    });
                }
            }
        }
    }, [selection, rows, pageSize, sheet, expanded, updateModeData]);

    useEffect(() =>
    {
        const cellFormatting: { [cellId: string]: Array<string> } = {};
        rows.forEach((row) =>
        {
            Object.entries(sheet.conditionalFormatting ?? {}).forEach(([id, format]) =>
            {
                const column = columns.find((column) => column.id === format.rule.columnId);
                if (column)
                {
                    const activeFormatClassNames = getActiveFormatClassNames(column, row, sheet, constants);
                    if (activeFormatClassNames.length > 0)
                    {
                        const cellId = `${column.id}_${row.id}`;
                        cellFormatting[cellId] = cellFormatting[cellId] ?? [];
                        cellFormatting[cellId].push(...activeFormatClassNames);
                    }
                }
            });
        });
        setConditionalFormatCells(cellFormatting);
    }, [rows, sheet, columns, constants]);

    useEffect(() =>
    {
        setCellHeights({});
    }, [sheetId]);

    useEffect(() =>
    {
        if (groupedViewEnabled === undefined)
        {
            updateModeData({ groupedViewEnabled: true });
        }
    }, [groupedViewEnabled, updateModeData]);

    useEffect(() =>
    {
        const handleCopyEvent = (event: ClipboardEvent) =>
        {
            if (selectedCell)
            {
                handleCopy(selectedCell.row, selectedCell.field);
                event.preventDefault();
            }
        }

        const handlePasteEvent = (event: ClipboardEvent) =>
        {
            if (selectedCell?.row && selectedCell?.field)
            {
                handlePaste(selectedCell.row, selectedCell.field);
                event.preventDefault();
            }
        }
        document.addEventListener("copy", handleCopyEvent);
        document.addEventListener("paste", handlePasteEvent);

        return () =>
        {
            document.removeEventListener("copy", handleCopyEvent);
            document.removeEventListener("paste", handlePasteEvent);
        }
    });

    useEffect(() =>
    {
        setContextCell({ row: "", column: "" });
        setCommentCellTarget(null);
        setCommentCell({ row: "", column: "" });
    }, [groupedViewEnabled, expanded]);

    return (
        <Box key={"root"} className={classes.root}>
            <SideBar onSetWidth={handleSideBarSetWidth} tabs={sideBarTabs}/>
            <Box className={classes.content} onContextMenu={handleContextMenu} ref={contentRef} sx={{width: width}}>
                <AddColumnDialog open={columnDialogOpen} close={closeColumnDialog} project={project} sheetId={sheetId ?? ""} />
                <AddSheetDialog open={addSheetDialogOpen} close={closeAddSheetDialog} createSheet={createSheet} />
                <SheetContextMenu target={contextCellTarget} onClose={handleCloseContextMenu} gridApi={gridApi.current}
                                  onCopy={handleCopy} onPaste={handlePaste} cursor={cursor} sheetId={sheetId ?? ""}
                                  cell={{ ...contextCell }} selection={statesSelection} />
                <SheetComments target={!editing ? commentCellTarget : null} onClose={handleCloseComments} sheetId={sheetId ?? ""} cell={{ ...commentCell }} />
                <ConditionalFormattingStyles sheet={sheet} />
                <DataGrid className={classes.gridView}
                    ref={grid} apiRef={gridApi}
                    columns={sortedColumns} rows={rows}
                    density={"compact"} showColumnRightBorder={true} showCellRightBorder={true}
                    sortingMode={"server"} sortModel={sheet.sortModel} onSortModelChange={handleSortModelChange}
                    filterMode={"server"} filterModel={filterModel} onFilterModelChange={handleFilterModelChange}
                    getCellClassName={getCellClassName} getRowClassName={getRowClassName}
                    onCellClick={handleCellClick}
                    onCellEditCommit={handleEditCellCommit}
                    onCellEditStart={handleCellEditStart}
                    onCellEditStop={handleCellEditStop}
                    columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={handleVisibilityModelChange}
                    selectionModel={selection} onSelectionModelChange={(selectionModel) => updateModeData({ selection: selectionModel })}
                    page={page} onPageChange={setPage}
                    pageSize={pageSize} onPageSizeChange={setPageSize}
                    rowsPerPageOptions={[25, 50, 100, 1000, 5000]}
                    onColumnWidthChange={handleColumnWidthChange}
                    onColumnOrderChange={handleColumnOrderChange}
                    getRowHeight={getRowHeight}
                    pinnedColumns={pinnedColumns}
                    onPinnedColumnsChange={setPinnedColumns}
                    componentsProps={{
                        toolbar: {
                            onClickAddColumn: handleClickAddColumn,
                            onClickToggleGroup: handleClickToggleGroup,
                        },
                        footer: {
                            onClickAddSheet: handleClickAddSheet,
                            onChangeActive: handleChangeActive
                        }
                    }}
                    components={{
                        Toolbar: SheetToolbar,
                        Footer: SheetTabs,
                        ColumnMenu: SheetColumnMenu
                    }}
                />
            </Box>
        </Box >
    );
}

const mapStateToProps = (store: never) => ({
    project: selectProject(store),
    projects: selectProjects(store),
    tags: selectTags(store) ?? {},
    constants: selectConstants(store) ?? {},
    sheets: selectSheets(store) ?? {},
    comments: selectComments(store),
    modeData: selectModeData(store) ?? {} as ISheetModeData
});
export default connect(mapStateToProps, {
    updateFocus,
    updateMode,
    updateModeData
})(GridView);
